import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import Typography from '@lib/components/Typography/Typography';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { Direction } from '@lib/hooks/useWindowScroll';
import {
  AiAssistantLauncher,
  useAiAssistantLauncher,
} from './AiAssistantLauncher';
import styles from './AiAssistantStickyBtn.module.scss';

const COLLAPSE_DELTA_THRESHOLD = 50;
const SCROLL_IDLE_TIMEOUT_MS = 500;

// Unlike the user app (window scroll), the admin dashboard scrolls an inner
// content container, so scroll events never reach the window target —
// a capture-phase listener observes them all instead. Being capture-phase
// means it sees every scroll on the page, including inside an open
// full-screen modal (a nested scroll's 'scroll' event doesn't bubble, but it
// still passes through window's capture phase on its way down to the
// target) — so a chatty modal like the AI assistant's transcript triggers
// this on every scroll tick even though the button it drives is hidden
// behind the modal and irrelevant while it's open. `skip` (true while the
// assistant modal is open) short-circuits that, so scrolling the transcript
// doesn't re-render this button on every tick.
function useAnyScroll(skip: boolean) {
  const [isScrolling, setIsScrolling] = useState(false);
  const [direction, setDirection] = useState<Direction | null>(null);
  const skipRef = useRef(skip);
  skipRef.current = skip;

  useEffect(() => {
    const lastPositions = new WeakMap<EventTarget, number>();
    let accumulatedDelta = 0;
    let idleTimeout: ReturnType<typeof setTimeout> | null = null;

    const handler = (event: Event) => {
      if (skipRef.current) return;
      const target = event.target as EventTarget | null;
      if (!target) return;
      const y =
        target instanceof Document
          ? window.scrollY
          : ((target as HTMLElement).scrollTop ?? 0);
      const lastY = lastPositions.get(target);
      lastPositions.set(target, y);
      if (lastY === undefined || y === lastY) return;

      setDirection(y > lastY ? Direction.Down : Direction.Up);
      accumulatedDelta += Math.abs(y - lastY);

      if (idleTimeout) clearTimeout(idleTimeout);
      idleTimeout = setTimeout(() => {
        accumulatedDelta = 0;
        setIsScrolling(false);
        setDirection(null);
      }, SCROLL_IDLE_TIMEOUT_MS);

      if (accumulatedDelta >= COLLAPSE_DELTA_THRESHOLD) {
        setIsScrolling(true);
      }
    };

    window.addEventListener('scroll', handler, true);
    return () => {
      window.removeEventListener('scroll', handler, true);
      if (idleTimeout) clearTimeout(idleTimeout);
    };
  }, []);

  return { isScrolling, direction };
}

function StickyBtn({ launcher }: { launcher: AiAssistantLauncher }) {
  const { t } = useTranslation();
  const { isScrolling, direction } = useAnyScroll(launcher.isOpen);
  // Drives the slide-in entrance. `usehooks-ts`'s useIsMounted is ref-based and
  // schedules no re-render, so reading it during render committed the button in
  // its off-screen (translateY(200%)) state and it only appeared after an
  // unrelated re-render — on a page too short to scroll, never. A one-shot mount
  // effect flips real state so the entrance always plays.
  const [entered, setEntered] = useState(false);
  useEffect(() => setEntered(true), []);

  useEffect(() => {
    // Shifts toasts above the sticky button (shared rule in lib Toast.scss).
    document.body.classList.add('with-scan-qr-sticky-btn');
    return () => document.body.classList.remove('with-scan-qr-sticky-btn');
  }, []);

  return (
    <button
      type="button"
      className={classNames(styles.button, {
        [styles.buttonVisible]: entered,
        [styles.buttonCollapsed]: isScrolling,
        [styles.buttonWhenUp]: direction === Direction.Up,
        [styles.buttonWhenDown]: direction === Direction.Down,
      })}
      onClick={() => launcher.open()}
      data-test-id="documents__ai-assistant-fab"
    >
      <MaterialIcon
        icon="auto_awesome"
        symbolsOutlined
        size="md-24"
        className={styles.icon}
      />
      <Typography variant="body" strong className={styles.label}>
        {t('ai-assistant-fab-label')}
      </Typography>
    </button>
  );
}

// Opens the dashboard's shared assistant (AiAssistantLauncherProvider owns the
// modal); renders nothing where the assistant isn't available.
export default function AiAssistantStickyBtn() {
  const launcher = useAiAssistantLauncher();
  return launcher ? <StickyBtn launcher={launcher} /> : null;
}
